import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { EstimationLogo } from '../assets/EstimationLogo';
import { BoxSpecifications, CostBreakdown as CostBreakdownType, Feature, CorrugatedType, SkuItem } from '../types';
import {
  calculateQuote,
  calculateMultiSkuQuote,
  calculateMultiQuantityQuote,
  bulkCalculateQuote,
  getErrorMessage,
  ApiValidationError,
  ApiServerError,
  NetworkError,
  ApiTimeoutError,
  QuantityTier
} from '../utils/api';
import { useBillingContext } from '../contexts/BillingContext';
import { StepperForm } from './StepperForm';
import { CostBreakdownCompact } from './CostBreakdownCompact';
import { SaveQuoteDialog } from './SaveQuoteDialog';
import { Button } from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { useAuth } from '../hooks/useAuth';

// Main Calculator Component
export function Calculator() {
  // Get auth context
  const { user } = useAuth();

  // Get billing context
  const { canCalculateQuote, refreshBilling } = useBillingContext();

  // State for input mode (dimensions or open size)
  const [inputMode, setInputMode] = useState<'dimensions' | 'openSize'>('dimensions');
  const location = useLocation();

  // State for box specifications
  const [specs, setSpecsInternal] = useState<BoxSpecifications>({
    boxType: 'tuck-top-snap-lock',
    length: 0, // Will appear empty in UI with proper handling
    width: 0,
    height: 0,
    dimensionUnit: 'in', // Default to inches
    material: 'cardboard',
    gsm: 300,
    corrugatedType: undefined, // Only set when material is 'corrugated'
    printing: {
      cmyk: 0,
      pantone: 0,
      metallic: 0
    },
    insidePrinting: {
      cmyk: 0,
      pantone: 0,
      metallic: 0
    },
    quantity: 100,
    features: [],
    featureAreas: {}, // Initialize empty feature areas
    laminationBothSides: false,
    whiteUnderprint: true, // Default ON for kraft materials
    vendorProfit: 20, // Default 20% vendor profit
    shippingOption: 'UPS',
    shippingCountry: 'USA',
    shippingType: 'express', // Default shipping type
  });

  // Alias for specs setter
  const setSpecs = setSpecsInternal;

  // State for open dimensions
  const [openDimensions, setOpenDimensions] = useState<{ openLength: number, openWidth: number }>({
    openLength: 0, // Start with empty/0 values
    openWidth: 0
  });

  const [breakdown, setBreakdown] = useState<CostBreakdownType | null>(null);
  const [allShippingOptions, setAllShippingOptions] = useState<Record<string, Record<string, { cost?: number } & Record<string, unknown>>> | null>(null); // Store all pre-calculated shipping options
  const [selectedShipping, setSelectedShipping] = useState<{ type: string; option: string } | null>(null); // Track selected shipping
  const [companyProfit, setCompanyProfit] = useState<number>(0); // Company profit margin percentage
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showSaveQuoteDialog, setShowSaveQuoteDialog] = useState(false);
  const [pendingMarginData, setPendingMarginData] = useState<{
    // Agent margin data
    agentMarginPercent?: number;
    agentCompanyProfit?: number;
    finalTotalWithAgentMargin?: number;
    costPerUnitWithAgentMargin?: number;
    // Admin margin data
    manufacturerMarginPercent?: number;
    companyMarginPercent?: number;
    manufacturerProfitAmount?: number;
    adminCompanyProfitAmount?: number;
    adminTotalWithProfit?: number;
    adminCostPerUnitWithProfit?: number;
  } | undefined>(undefined);
  // Multi-quantity support
  const [multiQtyEnabled, setMultiQtyEnabled] = useState(false);
  const [qtyOptions, setQtyOptions] = useState<number[]>([]);
  const [multiResults, setMultiResults] = useState<Array<{ qty: number; breakdown: CostBreakdownType }>>([]);
  const [selectedQty, setSelectedQty] = useState<number | null>(null);
  const lastPrefillKey = React.useRef<string | null>(null);

  // Order mode support (single, compare quantities, multi-design)
  const [orderMode, setOrderMode] = useState<'single' | 'compare' | 'multiDesign'>('single');
  const [skus, setSkus] = useState<SkuItem[]>([]);
  const [quantityTiers, setQuantityTiers] = useState<number[]>([]);
  const [selectedTierIndex, setSelectedTierIndex] = useState(0);
  const [tierSavings, setTierSavings] = useState<number[]>([]);
  const [multiQuantityResults, setMultiQuantityResults] = useState<QuantityTier[]>([]);

  // States for form change tracking and button management
  const [hasFormChanged, setHasFormChanged] = useState<boolean>(false);
  const [hasSuccessfulCalculation, setHasSuccessfulCalculation] = useState<boolean>(false);
  const [hasUnsavedMargins, setHasUnsavedMargins] = useState<boolean>(false);

  // Navigation protection for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedMargins) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedMargins]);

  // Apply prefill from navigation state (Create Similar or Continue Draft)
  useEffect(() => {
    const state = location.state as Record<string, unknown>;
    if (state?.prefill && state?.prefillKey !== lastPrefillKey.current) {
      try {
        setSpecs(state.prefill);
        if (state.prefillOpenDimensions) {
          setOpenDimensions(state.prefillOpenDimensions);
        }
        if (state.prefillInputMode) {
          setInputMode(state.prefillInputMode);
        }

        // If coming from a draft, also set the breakdown and variants
        if (state.fromDraft) {
          if (state.prefillBreakdown) {
            setBreakdown(state.prefillBreakdown);
            setHasSuccessfulCalculation(true);
          }

          // Handle multi-quantity drafts
          if (state.prefillVariants && Array.isArray(state.prefillVariants)) {
            const variants = state.prefillVariants as Array<{ quantity: number; costBreakdown?: CostBreakdownType }>;
            const quantities = variants.map((v) => v.quantity);

            setMultiQtyEnabled(true);
            setQtyOptions(quantities);

            // Convert variants to multi-results format
            const results = variants.map((v) => ({
              qty: v.quantity,
              breakdown: v.costBreakdown || (state.prefillBreakdown as CostBreakdownType)
            }));
            setMultiResults(results);

            // Set the first quantity as selected
            if (quantities.length > 0) {
              setSelectedQty(quantities[0]);
              if (results[0]?.breakdown) {
                setBreakdown(results[0].breakdown);
              }
            }
          }
        } else {
          // Clear any existing result for "Create Similar"
          setBreakdown(null);
          setHasSuccessfulCalculation(false);
          setMultiQtyEnabled(false);
          setQtyOptions([]);
          setMultiResults([]);
          setSelectedQty(null);
        }

        setError(null);
        setRetryCount(0);
        setHasFormChanged(false);
        lastPrefillKey.current = state.prefillKey || 'applied';
      } catch {
        // Ignore invalid prefill payloads; they can occur on stale navigation entries.
      }
    }
  }, [location.state, setSpecs]);
  // Force-remount key for StepperForm to reset to step one
  const [formResetKey, setFormResetKey] = useState(0);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    // Mark form as changed whenever any input changes
    setHasFormChanged(true);

    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      const featureValue = value as Feature;

      let features;
      let updatedFeatureAreas = { ...specs.featureAreas };

      if (checkbox.checked) {
        // Special handling for coating options - only allow one at a time
        if (featureValue === 'gloss-coating' || featureValue === 'matte-coating' || featureValue === 'soft-touch-coating') {
          features = specs.features.filter(f => f !== 'gloss-coating' as Feature && f !== 'matte-coating' as Feature && f !== 'soft-touch-coating' as Feature);
          features.push(featureValue);
        } else {
          features = [...specs.features, featureValue];

          // Pre-fill default area (2x2) for features that require area input when they're enabled
          const areaFeatures: Feature[] = ['embossing', 'debossing', 'foiling', 'spot-uv', 'pvc-window'];
          if (areaFeatures.includes(featureValue)) {
            // Only set default if no area exists yet
            if (!updatedFeatureAreas[featureValue as keyof typeof updatedFeatureAreas] ||
              (updatedFeatureAreas[featureValue as keyof typeof updatedFeatureAreas]?.length === 0 &&
                updatedFeatureAreas[featureValue as keyof typeof updatedFeatureAreas]?.width === 0)) {
              updatedFeatureAreas = {
                ...updatedFeatureAreas,
                [featureValue]: { length: 2, width: 2 }
              };
            }
          }
        }
      } else {
        features = specs.features.filter(f => f !== featureValue);
        // Clear the area when feature is unchecked
        const areaFeatures: Feature[] = ['embossing', 'debossing', 'foiling', 'spot-uv', 'pvc-window'];
        if (areaFeatures.includes(featureValue)) {
          const { [featureValue as keyof typeof updatedFeatureAreas]: _removed, ...rest } = updatedFeatureAreas;
          updatedFeatureAreas = rest as BoxSpecifications['featureAreas'];
        }
      }
      setSpecs({ ...specs, features, featureAreas: updatedFeatureAreas });
    } else if (type === 'number') {
      if (name === 'openLength' || name === 'openWidth') {
        setOpenDimensions({
          ...openDimensions,
          [name]: Number(value)
        });
      } else {
        setSpecs({ ...specs, [name]: Number(value) });
      }
    } else if (name === 'insert') {
      // Handle insert specification object directly
      setSpecs({ ...specs, insert: value as BoxSpecifications['insert'] });
      return;
    } else {
      const updatedSpecs = { ...specs, [name]: value };

      // Handle material changes
      if (name === 'material') {
        if (value === 'corrugated') {
          // Set default corrugated type and clear inside printing if single-face
          updatedSpecs.corrugatedType = 'single-kraft';
          updatedSpecs.insidePrinting = { cmyk: 0, pantone: 0, metallic: 0 };
          updatedSpecs.whiteUnderprint = true; // Enable for kraft variants
        } else if (value === 'kraft') {
          updatedSpecs.whiteUnderprint = true; // Enable for kraft
          updatedSpecs.corrugatedType = undefined;
        } else {
          // Clear corrugated type and white underprint for non-kraft materials
          updatedSpecs.corrugatedType = undefined;
          updatedSpecs.whiteUnderprint = false;
        }
      }

      // Handle corrugated type changes
      if (name === 'corrugatedType') {
        // Clear inside printing for single-face corrugated
        if (value === 'single-kraft' || value === 'single-cardboard') {
          updatedSpecs.insidePrinting = { cmyk: 0, pantone: 0, metallic: 0 };
        }
        // Enable white underprint for kraft variants
        if (value === 'single-kraft' || value === 'double-kraft') {
          updatedSpecs.whiteUnderprint = true;
        } else {
          updatedSpecs.whiteUnderprint = false;
        }
      }

      setSpecs(updatedSpecs);
    }
  };

  const handleLaminationBothSidesChange = (value: boolean) => {
    setHasFormChanged(true);
    setSpecs({ ...specs, laminationBothSides: value });
  };

  const handleWhiteUnderprintChange = (value: boolean) => {
    setHasFormChanged(true);
    setSpecs({ ...specs, whiteUnderprint: value });
  };

  const handleCorrugatedTypeChange = (corrugatedType: CorrugatedType) => {
    const updatedSpecs = { ...specs, corrugatedType };

    setHasFormChanged(true);

    // Clear inside printing for single-face corrugated
    if (corrugatedType === 'single-kraft' || corrugatedType === 'single-cardboard') {
      updatedSpecs.insidePrinting = { cmyk: 0, pantone: 0, metallic: 0 };
    }

    // Enable white underprint for kraft variants
    if (corrugatedType === 'single-kraft' || corrugatedType === 'double-kraft') {
      updatedSpecs.whiteUnderprint = true;
    } else {
      updatedSpecs.whiteUnderprint = false;
    }

    setSpecs(updatedSpecs);
  };

  const toggleInputMode = () => {
    setInputMode(prev => prev === 'dimensions' ? 'openSize' : 'dimensions');
    setHasFormChanged(true);
    // Reset breakdown when switching modes
    setBreakdown(null);
    setHasSuccessfulCalculation(false);
  };

  // No transformation needed - backend now returns the exact structure we need
  const extractCostBreakdown = (apiResponse: { data: { costBreakdown: CostBreakdownType; warnings?: string[]; metadata?: { pricingVersion?: string; calculatedAt?: string; processingTime?: number } } }): CostBreakdownType => {
    const breakdown = apiResponse.data.costBreakdown;

    // Add warnings and metadata from API response
    return {
      ...breakdown,
      warnings: apiResponse.data.warnings || [],
      metadata: {
        pricingVersion: apiResponse.data.metadata?.pricingVersion,
        calculatedAt: apiResponse.data.metadata?.calculatedAt,
        processingTime: apiResponse.data.metadata?.processingTime
      }
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (loading) return; // Prevent multiple simultaneous requests

    // Check quota availability
    if (!canCalculateQuote) {
      setError('You have reached your quote limit. Please upgrade your plan or wait for the next billing period.');
      return;
    }

    setLoading(true);
    setBreakdown(null);
    setError(null);

    try {
      // Check if quota is available (backend will handle actual consumption with deduplication)
      if (!canCalculateQuote) {
        setError('You have reached your quote limit. Please upgrade your plan or contact support.');
        setLoading(false);
        return;
      }

      // Choose bulk vs single
      // Backend handles quota tracking with 5-minute hash-based deduplication
      if (multiQtyEnabled && qtyOptions.filter(q => Number.isFinite(q) && q > 0).length > 0) {
        // Auto-correct shipping option based on destination for express shipping
        const correctedSpecs = { ...specs };
        if (correctedSpecs.shippingType === 'express' && correctedSpecs.shippingCountry !== 'USA') {
          // UPS only supports USA destinations, switch to DHL for UK/Dubai
          if (correctedSpecs.shippingOption === 'UPS') {
            correctedSpecs.shippingOption = 'DHL';
          }
        }

        const quantities = Array.from(new Set(qtyOptions.filter(q => Number.isFinite(q) && q > 0))).sort((a, b) => a - b).slice(0, 3);
        const apiResponse = await bulkCalculateQuote(
          correctedSpecs,
          quantities,
          inputMode === 'openSize' ? openDimensions : undefined
        );
        if (!apiResponse.success) throw new Error(apiResponse.error?.message || 'Bulk calculation failed');
        const variants = apiResponse.data.variants || [];
        const results = variants.map((v: { quantity: number; costBreakdown: CostBreakdownType }) => ({ qty: v.quantity, breakdown: v.costBreakdown }));
        setMultiResults(results);
        const first = results[0];
        if (apiResponse.data.billingInfo && first?.breakdown) {
          first.breakdown.billingInfo = apiResponse.data.billingInfo;
        }
        setSelectedQty(first?.qty || null);
        setBreakdown(first?.breakdown || null);
        setRetryCount(0);
        setHasFormChanged(false);
        setHasSuccessfulCalculation(true);

        // Refresh billing status to get updated quota from backend
        refreshBilling();
      } else {
        // Auto-correct shipping option based on destination for express shipping
        const correctedSpecs = { ...specs };
        if (correctedSpecs.shippingType === 'express' && correctedSpecs.shippingCountry !== 'USA') {
          // UPS only supports USA destinations, switch to DHL for UK/Dubai
          if (correctedSpecs.shippingOption === 'UPS') {
            correctedSpecs.shippingOption = 'DHL';
          }
        }

        let apiResponse;
        let costBreakdown: CostBreakdownType | null;

        // Use multi-quantity endpoint when comparing quantity tiers
        if (orderMode === 'compare' && quantityTiers.length > 0) {
          const multiQtyResponse = await calculateMultiQuantityQuote(
            correctedSpecs,
            quantityTiers,
            inputMode === 'openSize' ? openDimensions : undefined
          );

          if (!multiQtyResponse.success) {
            throw new Error(multiQtyResponse.error?.message || 'Multi-quantity calculation failed');
          }

          // Store all tier results
          setMultiQuantityResults(multiQtyResponse.data.tiers);
          setTierSavings(multiQtyResponse.data.tiers.map(t => t.savingsPercent));
          // Also populate multiResults for SaveQuoteDialog and CostBreakdownCompact
          setMultiResults(multiQtyResponse.data.tiers.map(t => ({
            qty: t.quantity,
            breakdown: t.costBreakdown as CostBreakdownType
          })));

          // Use the first tier as default selection
          const firstTier = multiQtyResponse.data.tiers[0];
          setSelectedTierIndex(0);
          // Set selectedQty for CostBreakdownCompact to display correct quantity
          setSelectedQty(quantityTiers[0] || specs.quantity);

          apiResponse = {
            success: true,
            data: {
              costBreakdown: firstTier.costBreakdown,
              allShippingOptions: firstTier.allShippingOptions
            }
          };
          costBreakdown = firstTier.costBreakdown as CostBreakdownType;
        }
        // Use multi-SKU endpoint when multiple designs are configured
        else if (orderMode === 'multiDesign' && skus.length > 0) {
          // Multi-SKU calculation with ganging optimization
          const multiSkuResponse = await calculateMultiSkuQuote(
            correctedSpecs,
            skus,
            inputMode === 'openSize' ? openDimensions : undefined
          );

          if (!multiSkuResponse.success) {
            throw new Error(multiSkuResponse.error?.message || 'Multi-SKU calculation failed');
          }

          apiResponse = multiSkuResponse;
          costBreakdown = extractCostBreakdown(multiSkuResponse);
        } else {
          // Single calculation
          apiResponse = await calculateQuote(
            correctedSpecs,
            inputMode === 'openSize' ? openDimensions : undefined
          );
          costBreakdown = extractCostBreakdown(apiResponse);
        }
        setBreakdown(costBreakdown);

        // Store all pre-calculated shipping options
        if (apiResponse.data?.allShippingOptions) {
          setAllShippingOptions(apiResponse.data.allShippingOptions);

          // Auto-select the lowest cost shipping option (respecting weight filters)
          const allOptions = apiResponse.data.allShippingOptions;
          const currentWeight = costBreakdown?.totalWeight || 0;
          let lowestOption: { type: string; option: string; cost: number } | null = null;

          // Check express shipping
          if (allOptions.express) {
            Object.entries(allOptions.express).forEach(([key, data]) => {
              // Apply weight-based filtering (same as StepperForm)
              const [carrier, destination] = key.split('_');

              // Only consider options for the selected destination
              if (destination && destination !== specs.shippingCountry) {
                return;
              }

              if (carrier === 'FEDEX' && destination === 'USA' && currentWeight < 22) {
                return; // Skip FEDEX to USA if weight < 22 kg
              }

              if (typeof data.cost !== 'number') return;

              if (!lowestOption || data.cost < lowestOption.cost) {
                lowestOption = { type: 'express', option: key, cost: data.cost };
              }
            });
          }

          // Check air freight
          if (allOptions.airFreight) {
            Object.entries(allOptions.airFreight).forEach(([key, data]) => {
              // Only consider matched destination or special DHL option
              if (key !== specs.shippingCountry && key !== 'DHL') {
                return;
              }

              if (typeof data.cost !== 'number') return;

              if (!lowestOption || data.cost < lowestOption.cost) {
                lowestOption = { type: 'airFreight', option: key, cost: data.cost };
              }
            });
          }

          // Check sea freight
          if (allOptions.seaFreight) {
            Object.entries(allOptions.seaFreight).forEach(([key, data]) => {
              // Only consider matched destination
              if (key !== specs.shippingCountry) {
                return;
              }

              if (typeof data.cost !== 'number') return;

              if (!lowestOption || data.cost < lowestOption.cost) {
                lowestOption = { type: 'seaFreight', option: key, cost: data.cost };
              }
            });
          }

          // Set the lowest option as selected and update breakdown
          if (lowestOption && costBreakdown) {
            setSelectedShipping({ type: lowestOption.type, option: lowestOption.option });

            // Calculate production subtotal with vendor profit
            const subtotalWithVendorProfit = costBreakdown.subtotalWithVendorProfit
              || (costBreakdown.productionSubtotal + (costBreakdown.vendorProfitAmount || 0))
              || 0;

            // Calculate total before company profit
            const totalBeforeCompanyProfit = subtotalWithVendorProfit + lowestOption.cost;

            // Apply company profit if set (default to 0% on initial calculation)
            const companyProfitAmount = (totalBeforeCompanyProfit * companyProfit) / 100;
            const newTotal = totalBeforeCompanyProfit + companyProfitAmount;
            const newCostPerUnit = newTotal / specs.quantity;

            // Parse shipping option to get carrier and destination
            const [carrier, destination] = lowestOption.option.split('_');
            const carrierNames: Record<string, string> = {
              'DHL': 'DHL Express',
              'UPS': 'UPS Express',
              'FEDEX': 'FedEx Express'
            };

            // Update breakdown with lowest shipping cost
            const updatedBreakdown = {
              ...costBreakdown,
              shippingCost: lowestOption.cost,
              shippingMethod: {
                type: lowestOption.type,
                option: lowestOption.option,
                carrier,
                carrierName: carrierNames[carrier] || carrier,
                destination
              },
              companyProfit,
              companyProfitAmount,
              totalBeforeCompanyProfit,
              total: newTotal,
              costPerUnit: newCostPerUnit
            };
            setBreakdown(updatedBreakdown);
          } else {
            setSelectedShipping(null);
          }
        }

        setMultiResults([]);
        setSelectedQty(null);
        setRetryCount(0);
        setHasFormChanged(false);
        setHasSuccessfulCalculation(true);

        // Refresh billing status to get updated quota from backend
        refreshBilling();
      }

    } catch (error) {
      console.error('Error calculating cost:', error);
      // Log full stack trace for debugging const assignment errors
      if (error instanceof Error) {
        console.error('Full stack trace:', error.stack);
        console.error('Error name:', error.name);
      }

      // Provide detailed user-friendly error messages with guidance
      let errorMessage = '';
      let canRetry = false;

      if (error instanceof ApiValidationError) {
        errorMessage = `Invalid input: ${error.message}`;
        if (error.field) {
          errorMessage += `\n\nPlease check the ${error.field} field and try again.`;
        }
      } else if (error instanceof NetworkError) {
        errorMessage = 'Unable to connect to the server';
        errorMessage += '\n\nPlease check your internet connection and try again.';
        canRetry = true;
      } else if (error instanceof ApiTimeoutError) {
        errorMessage = 'The request is taking too long to process';
        errorMessage += '\n\nThis might be due to server load. Please try again in a moment.';
        canRetry = true;
      } else if (error instanceof ApiServerError) {
        if (error.code === 'CONFIG_ERROR') {
          errorMessage = 'Pricing configuration is temporarily unavailable';
          errorMessage += '\n\nPlease try again in a few minutes. If the issue persists, contact support.';
          canRetry = true;
        } else if (error.code === 'CALCULATION_ERROR') {
          errorMessage = 'Unable to calculate the quote with the provided specifications';
          errorMessage += '\n\nPlease review your inputs and try again.';
        } else {
          errorMessage = `Server error: ${error.message}`;
          errorMessage += '\n\nPlease try again. If the issue persists, contact support.';
          canRetry = true;
        }
      } else {
        errorMessage = getErrorMessage(error);
        errorMessage += '\n\nPlease try again. If the issue persists, contact support.';
        canRetry = true;
      }

      setError(errorMessage);
      setBreakdown(null);
      setHasSuccessfulCalculation(false);

      // Auto-retry for network issues (max 2 retries)
      if (canRetry && retryCount < 2 && (error instanceof NetworkError || error instanceof ApiTimeoutError)) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          handleSubmit(e);
        }, 2000 * (retryCount + 1)); // Exponential backoff: 2s, 4s
      }

    } finally {
      setLoading(false);
    }
  };

  // Handler for when StepperForm calculates new open dimensions
  const handleOpenDimensionsCalculated = (calculatedDimensions: { openLength: number; openWidth: number }) => {
    setOpenDimensions(calculatedDimensions);
  };

  // Manual retry function for user-initiated retries
  const handleRetry = () => {
    setRetryCount(0);
    setError(null);
    const fakeEvent = { preventDefault: () => { } } as React.FormEvent;
    handleSubmit(fakeEvent);
  };

  // Clear error state
  const clearError = () => {
    setError(null);
    setRetryCount(0);
  };

  // Reset calculator for new calculation (reset inputs to defaults as well)
  const handleReset = () => {
    // Reset results/state
    setBreakdown(null);
    setError(null);
    setRetryCount(0);
    setHasFormChanged(false);
    setHasSuccessfulCalculation(false);

    // Reset input mode and all inputs to defaults
    setInputMode('dimensions');
    setSpecs({
      boxType: 'tuck-top-snap-lock',
      length: 0, // Reset to 0 (empty)
      width: 0,
      height: 0,
      dimensionUnit: 'in',
      material: 'cardboard',
      gsm: 300,
      corrugatedType: undefined,
      printing: {
        cmyk: 0,
        pantone: 0,
        metallic: 0
      },
      insidePrinting: {
        cmyk: 0,
        pantone: 0,
        metallic: 0
      },
      quantity: 100,
      features: [],
      featureAreas: {},
      laminationBothSides: false,
      vendorProfit: 20,
      shippingOption: 'UPS',
      shippingCountry: 'USA',
      shippingType: 'express',
    });
    setOpenDimensions({ openLength: 0, openWidth: 0 });
    // Reset multi-SKU state
    setOrderMode('single');
    setSkus([]);
    // Force StepperForm to reset its internal active tab to first step
    setFormResetKey((k) => k + 1);
  };

  const handlePrintingChange = (
    type: 'cmyk' | 'pantone' | 'metallic',
    value: number,
    isInside: boolean
  ) => {
    const target = isInside ? 'insidePrinting' : 'printing';
    setHasFormChanged(true);
    setSpecs(prev => ({
      ...prev,
      [target]: {
        ...prev[target],
        [type]: value
      }
    }));
  };

  const handleFeatureAreaChange = (
    feature: 'embossing' | 'debossing' | 'foiling' | 'spot-uv' | 'pvc-window',
    length: number,
    width: number
  ) => {
    setHasFormChanged(true);
    setSpecs(prev => ({
      ...prev,
      featureAreas: {
        ...prev.featureAreas,
        [feature]: { length, width }
      }
    }));
  };

  // Handle vendor profit changes - different behavior for agents vs admins
  const handleVendorProfitChange = (manufacturerProfit: number, companyMargin?: number) => {
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'company_admin';

    if (isAdmin && companyMargin !== undefined) {
      // Admin mode: Update local state only, no API call needed
      // The CostBreakdownCompact and CostBreakdownModal already handle local calculations
      setHasUnsavedMargins(true);
    } else {
      // Agent mode: Single profit value, update specs locally (no API call)
      const updatedSpecs = { ...specs, vendorProfit: manufacturerProfit };
      setSpecs(updatedSpecs);
      setHasUnsavedMargins(true);
    }
  };

  // Handle company profit margin change - update totals instantly
  const handleCompanyProfitChange = (newProfit: number) => {
    setCompanyProfit(newProfit);

    if (!breakdown) return;

    // Calculate production subtotal with vendor profit (base before shipping and company profit)
    // If API doesn't provide subtotalWithVendorProfit, calculate it manually
    const subtotalWithVendorProfit = breakdown.subtotalWithVendorProfit
      || (breakdown.productionSubtotal + (breakdown.vendorProfitAmount || 0))
      || 0;
    const shippingCost = breakdown.shippingCost || 0;

    // Calculate company profit amount
    const totalBeforeCompanyProfit = subtotalWithVendorProfit + shippingCost;
    const companyProfitAmount = (totalBeforeCompanyProfit * newProfit) / 100;

    // Calculate new total with company profit
    const newTotal = totalBeforeCompanyProfit + companyProfitAmount;
    const newCostPerUnit = newTotal / specs.quantity;

    // Update breakdown with new company profit
    const updatedBreakdown = {
      ...breakdown,
      companyProfit: newProfit,
      companyProfitAmount,
      totalBeforeCompanyProfit,
      total: newTotal,
      costPerUnit: newCostPerUnit
    };

    setBreakdown(updatedBreakdown);
  };

  // Handle tier selection change (for multi-quantity comparison mode)
  const handleTierSelect = (index: number) => {
    if (index < 0 || index >= multiQuantityResults.length) return;

    setSelectedTierIndex(index);
    // Update selectedQty so CostBreakdownCompact shows correct quantity
    const tierQuantity = quantityTiers[index];
    if (tierQuantity) {
      setSelectedQty(tierQuantity);
    }
    const tier = multiQuantityResults[index];
    const tierBreakdown = tier.costBreakdown as CostBreakdownType;
    const tierShippingOptions = tier.allShippingOptions as typeof allShippingOptions;

    // Update breakdown with selected tier's data
    setBreakdown(tierBreakdown);
    setAllShippingOptions(tierShippingOptions);

    // Auto-select lowest cost shipping for this tier
    if (tierShippingOptions) {
      const currentWeight = tierBreakdown?.totalWeight || 0;
      let lowestOption: { type: string; option: string; cost: number } | null = null;

      // Check express shipping
      if (tierShippingOptions.express) {
        Object.entries(tierShippingOptions.express).forEach(([key, data]) => {
          const [carrier, destination] = key.split('_');

          // Only consider options for the selected destination
          if (destination && destination !== specs.shippingCountry) return;

          // Weight-based filtering
          if (carrier === 'FEDEX' && destination === 'USA' && currentWeight < 22) return;

          if (typeof data.cost !== 'number') return;

          if (!lowestOption || data.cost < lowestOption.cost) {
            lowestOption = { type: 'express', option: key, cost: data.cost };
          }
        });
      }

      // Check air freight
      if (tierShippingOptions.airFreight) {
        Object.entries(tierShippingOptions.airFreight).forEach(([key, data]) => {
          if (key !== specs.shippingCountry && key !== 'DHL') return;
          if (typeof data.cost !== 'number') return;

          if (!lowestOption || data.cost < lowestOption.cost) {
            lowestOption = { type: 'airFreight', option: key, cost: data.cost };
          }
        });
      }

      // Check sea freight
      if (tierShippingOptions.seaFreight) {
        Object.entries(tierShippingOptions.seaFreight).forEach(([key, data]) => {
          if (key !== specs.shippingCountry) return;
          if (typeof data.cost !== 'number') return;

          if (!lowestOption || data.cost < lowestOption.cost) {
            lowestOption = { type: 'seaFreight', option: key, cost: data.cost };
          }
        });
      }

      // Set the lowest option and update breakdown with new shipping cost
      if (lowestOption && tierBreakdown) {
        setSelectedShipping({ type: lowestOption.type, option: lowestOption.option });

        // Recalculate totals with new shipping cost
        const subtotalWithVendorProfit = tierBreakdown.subtotalWithVendorProfit
          || (tierBreakdown.productionSubtotal + (tierBreakdown.vendorProfitAmount || 0))
          || 0;
        const totalBeforeCompanyProfit = subtotalWithVendorProfit + lowestOption.cost;
        const companyProfitAmount = (totalBeforeCompanyProfit * companyProfit) / 100;
        const newTotal = totalBeforeCompanyProfit + companyProfitAmount;
        const tierQuantity = quantityTiers[index] || specs.quantity;
        const newCostPerUnit = newTotal / tierQuantity;

        // Parse shipping option
        const [carrier, destination] = lowestOption.option.split('_');
        const carrierNames: Record<string, string> = {
          'DHL': 'DHL Express',
          'UPS': 'UPS Express',
          'FEDEX': 'FedEx Express'
        };

        // Update breakdown with lowest shipping cost
        const updatedBreakdown = {
          ...tierBreakdown,
          shippingCost: lowestOption.cost,
          shippingMethod: {
            type: lowestOption.type,
            option: lowestOption.option,
            carrier,
            carrierName: carrierNames[carrier] || carrier,
            destination
          },
          companyProfit,
          companyProfitAmount,
          totalBeforeCompanyProfit,
          total: newTotal,
          costPerUnit: newCostPerUnit
        };
        setBreakdown(updatedBreakdown);
      }
    } else if (tier.bestShipping) {
      // Fallback to bestShipping if available
      setSelectedShipping({
        type: tier.bestShipping.type,
        option: tier.bestShipping.key
      });
    }
  };

  // Handle shipping option selection - instant update (no API call)
  const handleShippingSelect = (type: string, option: string) => {
    if (!breakdown || !allShippingOptions) return;

    setSelectedShipping({ type, option });

    // Parse express shipping compound keys (e.g., "DHL_UK" → carrier="DHL", destination="UK")
    let shippingOption = option;
    let shippingCountry = specs.shippingCountry;

    if (type === 'express' && option.includes('_')) {
      const [carrier, destination] = option.split('_');
      shippingOption = carrier;
      shippingCountry = destination;
    }

    // Update specs with new shipping selection
    const updatedSpecs = {
      ...specs,
      shippingType: type as 'express' | 'airFreight' | 'seaFreight',
      shippingOption: shippingOption,
      shippingCountry: shippingCountry,
    };
    setSpecs(updatedSpecs);

    // Get new shipping cost from pre-calculated options
    const shippingData = allShippingOptions[type]?.[option];
    if (!shippingData || typeof shippingData.cost !== 'number') return;

    const newShippingCost = shippingData.cost;

    // Calculate production subtotal with vendor profit (base before shipping and company profit)
    // If API doesn't provide subtotalWithVendorProfit, calculate it manually
    const subtotalWithVendorProfit = breakdown.subtotalWithVendorProfit
      || (breakdown.productionSubtotal + (breakdown.vendorProfitAmount || 0))
      || 0;

    // Calculate total before company profit
    const totalBeforeCompanyProfit = subtotalWithVendorProfit + newShippingCost;

    // Apply company profit if set
    const companyProfitAmount = (totalBeforeCompanyProfit * companyProfit) / 100;
    const newTotal = totalBeforeCompanyProfit + companyProfitAmount;
    const newCostPerUnit = newTotal / specs.quantity;

    // Parse shipping option to get carrier and destination
    const [carrier, destination] = option.split('_');
    const carrierNames: Record<string, string> = {
      'DHL': 'DHL Express',
      'UPS': 'UPS Express',
      'FEDEX': 'FedEx Express'
    };

    // Update breakdown instantly
    const updatedBreakdown = {
      ...breakdown,
      shippingCost: newShippingCost,
      shippingMethod: {
        type,
        option,
        carrier,
        carrierName: carrierNames[carrier] || carrier,
        destination
      },
      companyProfit,
      companyProfitAmount,
      totalBeforeCompanyProfit,
      total: newTotal,
      costPerUnit: newCostPerUnit
    };

    setBreakdown(updatedBreakdown);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Page Title */}
      <div className="px-6 py-4 border-b border-border">
        <h1 className="text-3xl font-bold tracking-tight">New Quote</h1>
        <p className="text-muted-foreground">
          Calculate package costs and generate quotes with detailed breakdowns.
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calculator Form - Left Side */}
          <div className="lg:col-span-1">
            <Card className="border-border bg-card shadow-sm min-h-[500px]">
              <StepperForm
                key={formResetKey}
                specs={specs}
                inputMode={inputMode}
                openDimensions={openDimensions}
                loading={loading}
                onInputModeToggle={toggleInputMode}
                onSpecsChange={handleChange}
                onLaminationBothSidesChange={handleLaminationBothSidesChange}
                onWhiteUnderprintChange={handleWhiteUnderprintChange}
                onPrintingChange={handlePrintingChange}
                onFeatureAreaChange={handleFeatureAreaChange}
                onCorrugatedTypeChange={handleCorrugatedTypeChange}
                onOpenDimensionsCalculated={handleOpenDimensionsCalculated}
                onSubmit={handleSubmit}
                // Pass button state
                hasFormChanged={hasFormChanged}
                hasSuccessfulCalculation={hasSuccessfulCalculation}
                // Shipping options support
                allShippingOptions={allShippingOptions}
                selectedShipping={selectedShipping}
                onShippingSelect={handleShippingSelect}
                weight={breakdown?.totalWeight}
                // Company profit margin
                companyProfit={companyProfit}
                onCompanyProfitChange={handleCompanyProfitChange}
                // Order mode support (single, compare, multiDesign)
                orderMode={orderMode}
                onOrderModeChange={(mode) => {
                  setHasFormChanged(true);
                  setOrderMode(mode);
                  // Reset calculation state when mode changes
                  setHasSuccessfulCalculation(false);
                  setMultiQuantityResults([]);
                  setTierSavings([]);
                }}
                skus={skus}
                onSkusChange={(newSkus) => {
                  setHasFormChanged(true);
                  setSkus(newSkus);
                }}
                quantityTiers={quantityTiers}
                onQuantityTiersChange={(tiers) => {
                  setHasFormChanged(true);
                  setQuantityTiers(tiers);
                }}
                selectedTierIndex={selectedTierIndex}
                onSelectedTierChange={handleTierSelect}
                tierSavings={tierSavings}
              />
            </Card>
          </div>

          {/* Results - Right Side */}
          <div className="lg:col-span-1">
            {error && (
              <Card className="mb-6 bg-destructive/10 border-destructive/20">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-destructive" />
                    <CardTitle className="text-destructive">Error</CardTitle>
                  </div>
                  <CardDescription className="text-destructive/80">
                    {error}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex gap-2 pt-0">
                  <Button
                    onClick={handleRetry}
                    disabled={loading}
                    size="sm"
                    variant="outline"
                    className="border-destructive/30 text-destructive hover:bg-destructive/10"
                  >
                    {loading ? 'Retrying...' : 'Try Again'}
                  </Button>
                  <Button
                    onClick={clearError}
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:bg-destructive/10"
                  >
                    Dismiss
                  </Button>
                </CardContent>
              </Card>
            )}

            {loading ? (
              <Card className="border-border bg-card shadow-sm h-fit min-h-[500px] flex items-center justify-center">
                <div className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-primary/10">
                    <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  </div>
                  <p className="text-base text-card-foreground font-medium">Calculating costs...</p>
                </div>
              </Card>
            ) : breakdown ? (
              <div className="space-y-4">
                <CostBreakdownCompact
                  breakdown={breakdown}
                  // Pass selected quantity in specs for correct display in details
                  specs={selectedQty ? { ...specs, quantity: selectedQty } : specs}
                  inputMode={inputMode}
                  openDimensions={openDimensions}
                  onProfitChange={handleVendorProfitChange}
                  onSaveQuote={(marginData) => {
                    setPendingMarginData(marginData);
                    setShowSaveQuoteDialog(true);
                  }}
                  quantityOptions={multiResults && multiResults.length > 0 ? multiResults.map(r => r.qty) : undefined}
                  selectedQuantity={selectedQty}
                  onSelectQuantity={(qty) => {
                    setSelectedQty(qty);
                    const match = multiResults.find(r => r.qty === qty);
                    if (match) setBreakdown(match.breakdown);
                  }}
                  isClassicUI={true}
                />

                <Button
                  onClick={handleReset}
                  variant="outline"
                  className="w-full"
                  size="default"
                >
                  Calculate Another
                </Button>
              </div>
            ) : (
              <Card className="border-border bg-card shadow-sm h-fit min-h-[500px] flex items-center justify-center">
                <div className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-primary/10">
                    <EstimationLogo className="w-6 h-6" variant="dark" />
                  </div>
                  <h3 className="text-lg font-semibold text-card-foreground mb-2">
                    Ready to Calculate
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Fill in your packaging details and click &quot;Calculate Cost&quot; to see a detailed breakdown.
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Save Quote Dialog */}
      {showSaveQuoteDialog && breakdown && (
        <SaveQuoteDialog
          isOpen={showSaveQuoteDialog}
          onClose={() => { setShowSaveQuoteDialog(false); setPendingMarginData(undefined); }}
          specifications={skus.length > 0 ? { ...specs, skus } : specs}
          breakdown={breakdown!}
          vendorProfit={specs.vendorProfit}
          variants={multiResults && multiResults.length > 0 ? multiResults.map(r => ({ quantity: r.qty, breakdown: r.breakdown })) : undefined}
          primaryQuantity={selectedQty || undefined}
          marginData={pendingMarginData}
          onSuccess={() => {
            setShowSaveQuoteDialog(false);
            setPendingMarginData(undefined);
            setHasUnsavedMargins(false);  // Reset unsaved margins flag
          }}
        />
      )}
    </div>
  );
}
